import React, { useMemo, useState } from "react";
import { Col, Input, Row, Select, Spin } from "antd";
import { useQuery } from "@apollo/client";
import useDebounce from "hooks/useDebounce";
import { NAVIGATION_TYPE, NAVIGATION_TYPE_LABELS } from "../../core/constants";
import { GET_SELECT_CAMPAIGN_LIST_QUERY } from "graphql/campaign/query";
import { GET_PRODUCTS_QUERY } from "graphql/product/query";
import { GET_CATEGORIES_LEVEL_3 } from "graphql/category/query";
import { GET_PRODUCT_BRAND } from "graphql/product/query";
import { SELLING_STATUS } from "pages/App/Product/constants";
import { PROMOTION_CAMPAIGN_STATUS } from "features/Campaign/constants";

const { Option } = Select;

function NavigationTargetSelect({
  navigationType = NAVIGATION_TYPE.NONE,
  targetID,
  targetName,
  url,
  onTypeChange,
  onTargetIDChange,
  onURLChange,
  disabled = false,
  error = {},
}) {
  const [productSearch, setProductSearch] = useState("");
  const [campaignSearch, setCampaignSearch] = useState("");
  const [categorySearch, setCategorySearch] = useState("");
  const [brandSearch, setBrandSearch] = useState("");

  const debouncedProductSearch = useDebounce(productSearch, 400);
  const debouncedCampaignSearch = useDebounce(campaignSearch, 400);
  const debouncedCategorySearch = useDebounce(categorySearch, 400);
  const debouncedBrandSearch = useDebounce(brandSearch, 400);

  // 1. Lấy danh sách Campaign đang hoạt động và có status là 'ongoing' hoặc 'upcoming' (sort theo ASC)
  const { data: campaignData, loading: loadingCampaigns } = useQuery(GET_SELECT_CAMPAIGN_LIST_QUERY, {
    variables: {
      filters: {
        query: debouncedCampaignSearch || undefined,
        isActive: true,
        statuses: [PROMOTION_CAMPAIGN_STATUS.ON_GOING, PROMOTION_CAMPAIGN_STATUS.UPCOMING],
      },
      pagination: { offset: 0, limit: 30, skipTotalCount: true },
      sort: [
        {
          by: "ID",
          sortType: "ASC",
        },
      ],
    },
    skip: navigationType !== NAVIGATION_TYPE.PROMOTION_CAMPAIGN,
    fetchPolicy: "cache-first",
  });

  // 2. Lấy danh sách Product đang kinh doanh (isActive: true, withCart: false, sellingStatus: AVAILABLE)
  const { data: productData, loading: loadingProducts } = useQuery(GET_PRODUCTS_QUERY, {
    variables: {
      withCart: false,
      filters: {
        query: debouncedProductSearch || undefined,
        isActive: true,
        sellingStatuses: [SELLING_STATUS.AVAILABLE],
      },
      pagination: { offset: 0, limit: 30, skipTotalCount: true },
    },
    skip: navigationType !== NAVIGATION_TYPE.PRODUCT_DETAIL,
    fetchPolicy: "cache-first",
  });

  // 3. Lấy danh mục ngành hàng ($req: CategoriesFilters, lấy tất cả cấp 1, 2, 3)
  const { data: categoryData, loading: loadingCategories } = useQuery(GET_CATEGORIES_LEVEL_3, {
    variables: {
      req: {
        query: debouncedCategorySearch || undefined,
        levels: [1, 2, 3],
        isActive: true,
      },
    },
    skip: navigationType !== NAVIGATION_TYPE.CATEGORY,
    fetchPolicy: "cache-first",
  });

  // 4. Lấy thương hiệu ($filters: BrandFilters!, $pagination: PaginationRequest!)
  const { data: brandData, loading: loadingBrands } = useQuery(GET_PRODUCT_BRAND, {
    variables: {
      filters: {
        query: debouncedBrandSearch || undefined,
        isActive: true,
      },
      pagination: { offset: 0, limit: 100 },
    },
    skip: navigationType !== NAVIGATION_TYPE.BRAND,
    fetchPolicy: "cache-first",
  });

  const campaigns = useMemo(() => {
    const list = campaignData?.promotionCampaign?.pagination?.promotionCampaigns || [];
    return list.filter(
      (c) => !c.status || [PROMOTION_CAMPAIGN_STATUS.ON_GOING, PROMOTION_CAMPAIGN_STATUS.UPCOMING].includes(c.status)
    );
  }, [campaignData]);

  const products = useMemo(() => {
    const list = productData?.product?.pagination?.products || [];
    return list.filter((p) => !p.sellingStatus || p.sellingStatus === SELLING_STATUS.AVAILABLE);
  }, [productData]);

  const categories = useMemo(() => {
    return categoryData?.category?.list || [];
  }, [categoryData]);

  const brands = useMemo(() => {
    return brandData?.brand?.pagination?.brands || [];
  }, [brandData]);

  return (
    <Row gutter={12}>
      <Col span={8}>
        <Select
          style={{ width: "100%" }}
          value={navigationType}
          onChange={(newType) => {
            if (newType !== navigationType) {
              onTypeChange(newType);
              onTargetIDChange(null, "");
              onURLChange("");
              setProductSearch("");
              setCampaignSearch("");
              setCategorySearch("");
              setBrandSearch("");
            }
          }}
          disabled={disabled}
        >
          {Object.entries(NAVIGATION_TYPE_LABELS).map(([key, label]) => (
            <Option key={key} value={key}>
              {label}
            </Option>
          ))}
        </Select>
      </Col>

      <Col span={16}>
        {navigationType === NAVIGATION_TYPE.NONE && (
          <Input disabled value="Không điều hướng" placeholder="Không điều hướng" />
        )}

        {navigationType === NAVIGATION_TYPE.PROMOTION_CAMPAIGN && (
          <Select
            showSearch
            allowClear
            style={{ width: "100%" }}
            placeholder="Tìm kiếm chương trình phù hợp"
            value={targetID || undefined}
            disabled={disabled}
            filterOption={false}
            onSearch={(val) => setCampaignSearch(val)}
            onChange={(val, option) => onTargetIDChange(val, option?.children || "")}
            notFoundContent={loadingCampaigns ? <Spin size="small" /> : "Không tìm thấy chương trình"}
            status={error.targetID ? "error" : undefined}
          >
            {targetID && !campaigns.some((c) => c.id === targetID) && (
              <Option key={targetID} value={targetID}>
                {targetName || `Chương trình #${targetID}`}
              </Option>
            )}
            {campaigns.map((c) => (
              <Option key={c.id} value={c.id}>
                {c.name}
              </Option>
            ))}
          </Select>
        )}

        {navigationType === NAVIGATION_TYPE.PRODUCT_DETAIL && (
          <Select
            showSearch
            allowClear
            style={{ width: "100%" }}
            placeholder="Tìm kiếm tên sản phẩm"
            value={targetID || undefined}
            disabled={disabled}
            filterOption={false}
            onSearch={(val) => setProductSearch(val)}
            onChange={(val, option) => onTargetIDChange(val, option?.children || "")}
            notFoundContent={loadingProducts ? <Spin size="small" /> : "Không tìm thấy sản phẩm"}
            status={error.targetID ? "error" : undefined}
          >
            {targetID && !products.some((p) => p.id === targetID) && (
              <Option key={targetID} value={targetID}>
                {targetName || `Sản phẩm #${targetID}`}
              </Option>
            )}
            {products.map((p) => (
              <Option key={p.id} value={p.id}>
                {p.name} ({p.code})
              </Option>
            ))}
          </Select>
        )}

        {navigationType === NAVIGATION_TYPE.CATEGORY && (
          <Select
            showSearch
            allowClear
            style={{ width: "100%" }}
            placeholder="Tìm kiếm ngành hàng"
            value={targetID || undefined}
            disabled={disabled}
            filterOption={false}
            onSearch={(val) => setCategorySearch(val)}
            onChange={(val, option) => onTargetIDChange(val, option?.children || "")}
            notFoundContent={loadingCategories ? <Spin size="small" /> : "Không tìm thấy ngành hàng"}
            status={error.targetID ? "error" : undefined}
          >
            {targetID && !categories.some((c) => c.id === targetID) && (
              <Option key={targetID} value={targetID}>
                {targetName || `Ngành hàng #${targetID}`}
              </Option>
            )}
            {categories.map((c) => (
              <Option key={c.id} value={c.id}>
                {c.name}
              </Option>
            ))}
          </Select>
        )}

        {navigationType === NAVIGATION_TYPE.BRAND && (
          <Select
            showSearch
            allowClear
            style={{ width: "100%" }}
            placeholder="Tìm kiếm thương hiệu"
            value={targetID || undefined}
            disabled={disabled}
            filterOption={false}
            onSearch={(val) => setBrandSearch(val)}
            onChange={(val, option) => onTargetIDChange(val, option?.children || "")}
            notFoundContent={loadingBrands ? <Spin size="small" /> : "Không tìm thấy thương hiệu"}
            status={error.targetID ? "error" : undefined}
          >
            {targetID && !brands.some((b) => b.id === targetID) && (
              <Option key={targetID} value={targetID}>
                {targetName || `Thương hiệu #${targetID}`}
              </Option>
            )}
            {brands.map((b) => (
              <Option key={b.id} value={b.id}>
                {b.name}
              </Option>
            ))}
          </Select>
        )}

        {navigationType === NAVIGATION_TYPE.LANDING_PAGE && (
          <Input
            value={url}
            disabled={disabled}
            placeholder="Nhập đường dẫn https://honganh.vn/khuyen-mai"
            onChange={(e) => onURLChange(e.target.value)}
            status={error.url ? "error" : undefined}
          />
        )}

        {navigationType === NAVIGATION_TYPE.APP_FEATURE && (
          <Input
            value={url}
            disabled={disabled}
            placeholder="Nhập link tính năng mới trên app (deeplink, vd: ha://warranty)"
            onChange={(e) => onURLChange(e.target.value)}
            status={error.url ? "error" : undefined}
          />
        )}

        {(error.targetID || error.url) && (
          <div style={{ color: "#ff4d4f", fontSize: "12px", marginTop: "4px" }}>{error.targetID || error.url}</div>
        )}
      </Col>
    </Row>
  );
}

export default React.memo(NavigationTargetSelect);
